const RETURN_TO_BASE_URL = 'http://localhost'
const MAX_RETURN_TO_LENGTH = 2048
const BLOCKED_RETURN_TO_PREFIXES = [
    '/payments/online/success',
    '/payments/online/failure',
    '/_clerk'
] as const

const CLERK_QUERY_PREFIX = '__clerk'

function parseInternalUrl(value: string): URL | null {
    try {
        return new URL(value, RETURN_TO_BASE_URL)
    } catch {
        return null
    }
}

export const normalizeReturnToPath = (value?: string | null): string | null => {
    if (!value) return null
    const trimmed = value.trim()
    if (!trimmed || trimmed.length > MAX_RETURN_TO_LENGTH) return null
    const url = parseInternalUrl(trimmed)
    if (!url || url.origin !== RETURN_TO_BASE_URL) return null
    const pathname = url.pathname || '/'
    if (!pathname.startsWith('/')) return null
    const isBlocked = BLOCKED_RETURN_TO_PREFIXES.some((prefix) =>
        pathname === prefix || pathname.startsWith(`${prefix}/`)
    )
    if (isBlocked) return null

    const sanitizedSearchParams = new URLSearchParams(url.searchParams)
    sanitizedSearchParams.delete('returnTo')
    for (const key of Array.from(sanitizedSearchParams.keys())) {
        if (key.startsWith(CLERK_QUERY_PREFIX)) {
            sanitizedSearchParams.delete(key)
        }
    }
    const search = sanitizedSearchParams.toString()
    return `${pathname}${search ? `?${search}` : ''}${url.hash}`
}

export const resolveSafeReturnTo = (value?: string | null, fallback = '/'): string => {
    const safeFallback = normalizeReturnToPath(fallback) ?? '/'
    return normalizeReturnToPath(value) ?? safeFallback
}

export const appendQueryParams = (
    href: string,
    params: Record<string, string | null | undefined>
): string => {
    const parsed = parseInternalUrl(href)
    if (!parsed) return href
    Object.entries(params).forEach(([key, value]) => {
        const normalized = value?.trim()
        if (!normalized) {
            parsed.searchParams.delete(key)
            return
        }
        parsed.searchParams.set(key, normalized)
    })
    return `${parsed.pathname}${parsed.search}${parsed.hash}`
}

export const buildReturnTo = (href: string, returnTo?: string | null) => {
    const normalizedReturnTo = normalizeReturnToPath(returnTo)
    if (!normalizedReturnTo) return href
    return appendQueryParams(href, {returnTo: normalizedReturnTo})
}

export const getPathWithSearch = (pathname: string, searchParams?: URLSearchParams | null) => {
    const params = new URLSearchParams(searchParams?.toString() ?? '')
    params.delete('returnTo')
    for (const key of Array.from(params.keys())) {
        if (key.startsWith(CLERK_QUERY_PREFIX)) {
            params.delete(key)
        }
    }
    const search = params.toString()
    return search ? `${pathname}?${search}` : pathname
}
